import { ConnectedPosition, CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  ViewChildren,
  QueryList,
  OnDestroy,
  ViewChild,
  Inject,
  DOCUMENT,
  TemplateRef,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Subscription, fromEvent, Observable, of, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { MenuEntryViewModel } from '@nimbus-library/features/menu/models/menu-entry.view-model';
import { MenuActions } from '../../state/menu.actions';
import { MenuState } from '../../state/menu.state';

@Component({
  standalone: false,
  selector: 'nimbus-menu-item',
  templateUrl: './menu-item.component.html',
  styleUrls: ['./menu-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuItemComponent implements OnInit, OnDestroy {
  @Input() public entry: MenuEntryViewModel;
  @Input() public overlayContent: TemplateRef<void>;

  public isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  @ViewChildren(MenuItemComponent) public subMenuItems: QueryList<MenuItemComponent>;
  @ViewChild(CdkConnectedOverlay, { static: true }) public connectedOverlay: CdkConnectedOverlay;

  public connectedPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'top', offsetX: 0, offsetY: -15 },
  ];

  public active$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showTooltip$: Observable<boolean>;
  public showSubEntries$: Observable<boolean>;
  public showAnimation$: Observable<boolean>;

  private readonly subscription$ = new Subscription();
  private mouseEventSubscription$;

  public constructor(
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly store: Store,
  ) {
    this.subscription$.add(this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.update()));
    this.showTooltip$ = this.active$.pipe(map((active) => active || !(this.entry.subEntries && this.entry.subEntries.length > 0)));
  }

  public ngOnInit(): void {
    this.showAnimation$ = this.entry.subEntries
      ? this.store
          .select(MenuState.activationHistory)
          .pipe(map((history) => !this.entry.subEntries.find((subEntry) => isEqual(subEntry, history.prev))))
      : of(false);
    this.update();

    this.showSubEntries$ =
      this.entry.subEntries && this.entry.subEntries.length > 0
        ? combineLatest(this.entry.subEntries.map((subEntry) => subEntry.filter$)).pipe(
            map((results) => results.filter((result) => !!result).length >= 1),
          )
        : of(true);
  }

  public update(): void {
    const active = this.entry.path ? this.router.isActive(this.entry.path, false) : false;
    this.active$.next(
      active ||
        (this.entry.subEntries && this.entry.subEntries.some((subEntry) => this.router.isActive(subEntry.path, false))) ||
        (this.entry.connectedPaths && this.entry.connectedPaths.some((connectedPath) => this.router.isActive(connectedPath, false))),
    );

    if (active) {
      this.store.dispatch(new MenuActions.SetActiveMenuEntry(this.entry));
    }
  }

  public navigate(ev: MouseEvent): void {
    this.store.dispatch(new MenuActions.Navigate(this.entry));
  }

  public overlayOpened(): void {
    const hostEl = (this.connectedOverlay.origin as CdkOverlayOrigin).elementRef.nativeElement as HTMLElement;
    const overlayEl = this.connectedOverlay.overlayRef.overlayElement;
    this.mouseEventSubscription$ = fromEvent(this.document, 'mousemove').subscribe((ev: MouseEvent) => {
      if (!(this.isMouseInElement(hostEl, ev.clientX, ev.clientY) || this.isMouseInElement(overlayEl, ev.clientX, ev.clientY))) {
        this.isOpen$.next(false);
      }
    });
  }

  public isMouseInElement(elem: HTMLElement, clientX: number, clientY: number): boolean {
    const rect = elem.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.left + rect.width && clientY >= rect.top && clientY <= rect.top + rect.height;
  }

  public open(): void {
    this.isOpen$.next(true);
  }

  public close(): void {
    this.isOpen$.next(false);
  }

  public overlayClosed(): void {
    this.mouseEventSubscription$.unsubscribe();
  }

  public ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
